type InviteStatus = "enviado" | "aceito" | "negado" | "cancelado" | "desvinculado";

interface InviteStatusConfig {
    label: string;
    colorClass: string;
    textStyle: string;
}

const STATUS_CONFIG: Record<InviteStatus, InviteStatusConfig> = {
    enviado: { label: "Pendente", colorClass: "text-warning-600", textStyle: "font-semibold" },
    aceito: { label: "Aceito", colorClass: "text-success-500", textStyle: "font-normal" },
    negado: { label: "Negado", colorClass: "text-danger-500", textStyle: "font-normal opacity-50" },
    cancelado: { label: "Cancelado", colorClass: "text-secondary-500", textStyle: "font-normal" },
    // Set directly by POST /leave_organization when an admin removes a
    // member with a resolvable invite_id (organization/routes.py) — the
    // primary removal path, distinct from the "aceito" + member_active=false
    // fallback below (which covers members with no resolvable Invite row).
    // Was previously unmapped here, falling through to the raw "desvinculado"
    // string with no styling.
    desvinculado: { label: "Desvinculado", colorClass: "text-secondary-500", textStyle: "font-normal opacity-50" },
};

const getConfig = (status: string | undefined): InviteStatusConfig | undefined =>
    status ? STATUS_CONFIG[status as InviteStatus] : undefined;

/** True when an accepted invite's member is no longer an active org member but status_invite is still "aceito" — the fallback listing path (no resolvable invite_id for that member, see get_organizations_invite) where removal never updates the Invite row directly. */
const isUnlinkedAccepted = (status: string | undefined, memberActive: boolean | undefined): boolean =>
    status === "aceito" && memberActive === false;

/** Returns the display label for an invite status. */
export const getInviteStatusLabel = (status: string | undefined, memberActive?: boolean): string =>
    isUnlinkedAccepted(status, memberActive) ? "Aceito (desvinculado)" : (getConfig(status)?.label ?? status ?? "-");

/** Returns a Tailwind text color class based on the invite status. */
export const getInviteStatusColorClass = (status: string | undefined, memberActive?: boolean): string =>
    isUnlinkedAccepted(status, memberActive) ? "text-secondary-500" : (getConfig(status)?.colorClass ?? "");

/** Returns Tailwind font/opacity classes based on the invite status. */
export const getInviteStatusTextStyle = (status: string | undefined): string =>
    getConfig(status)?.textStyle ?? "font-normal";
